import React, { useState, useMemo } from "react";
import { Typography, Segmented } from "antd";
import {
    PlusCircleOutlined,
    BankOutlined,
    ShopOutlined,
    PieChartOutlined,
    ContainerOutlined,
    DatabaseOutlined,
    CalendarOutlined,
    AuditOutlined,
    FileTextOutlined,
    CheckSquareOutlined,
    SafetyCertificateOutlined,
    CalculatorOutlined,
    FileSearchOutlined,
    TableOutlined,
    PartitionOutlined,
    TeamOutlined,
    EnvironmentOutlined,
    DesktopOutlined,
    ShoppingOutlined,
    ReconciliationOutlined,
    SolutionOutlined,
    AppstoreOutlined,
    BorderOuterOutlined,
    LayoutOutlined,
    AlertOutlined,
    HistoryOutlined,
    MessageOutlined,
    DollarOutlined,
    DeploymentUnitOutlined,
    SettingOutlined,
    UserOutlined,
} from "@ant-design/icons";
import { AirplaneOutlined } from "../../../../components/icons/SvgIcons";
import { useNavigate } from "react-router-dom";
import WidgetWrapper from "../../components/WidgetWrapper";
import { useAuth } from "../../../../hooks/useAuth";

// Import Styles
import PortalStyleClassic from "./portal/PortalStyleClassic";
import PortalStyleVibrant from "./portal/PortalStyleVibrant";
import PortalStyleDirect from "./portal/PortalStyleDirect";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { flightOperationFlightService } from "../../../../api/enhancedApi";

dayjs.extend(utc);

/**
 * 模块权限映射：key -> 所需权限
 * 如果用户没有对应权限，该模块将不会显示在门户中。
 * 设置为 null 表示无需权限，所有用户可见。
 */
const MODULE_PERMISSIONS = {
    ops: "flight_operation:read_basic",
    settlement: "settlement:read",
    sales: "mawb:read",
    procurement: "contract:read",
    stats: "financial:read",
    uld: "uld:read",
    basic: "masterdata:read",
    system: "user:read",
};

const PORTAL_ROW_HEIGHT = 3; // 每行卡片占 dashboard grid 的 h 单位数

const SystemPortalWidget = (props) => {
    const { onRequestResize, ...restProps } = props;
    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const [currentStyle, setCurrentStyle] = useState("vibrant");
    const [opsMetrics, setOpsMetrics] = useState({ total: "-", active: "-", abnormal: "-" });

    // 判断用户是否有 OPS 权限，以决定是否获取 OPS Metrics
    const hasOpsPermission = hasPermission(MODULE_PERMISSIONS.ops);

    // Fetch OPS Metrics (仅当用户有权限时)
    React.useEffect(() => {
        // 如果用户没有 OPS 权限，不发起请求，避免 403 错误
        if (!hasOpsPermission) {
            return;
        }

        const fetchOpsMetrics = async () => {
            try {
                // 使用 UTC 日期，因为后端数据库存储的是 UTC 时间
                const today = dayjs().utc().format("YYYY-MM-DD");
                console.log("[DEBUG] SystemPortalWidget 查询日期 (UTC):", today);
                
                // 1. Total Today - 今日航班（限制状态）
                const totalReq = flightOperationFlightService.getFlightOperations({
                    start_date: today,
                    end_date: today,
                    status: "COMPLETED,DELAYED,DEPARTED,SCHEDULED",
                    page_size: 1, // We only need total count
                });

                // 2. Active (In Air) - 执行中航班（不限日期，只看状态）
                const activeReq = flightOperationFlightService.getFlightOperations({
                    status: "DEPARTED",
                    page_size: 1,
                });

                // 3. Abnormal (Delayed/Diverted) - 异常航班（今日 + 异常状态）
                const abnormalReq = flightOperationFlightService.getFlightOperations({
                    start_date: today,
                    end_date: today,
                    status: "DELAYED,DIVERTED,CANCELLED",
                    page_size: 1,
                });

                const [totalRes, activeRes, abnormalRes] = await Promise.all([totalReq, activeReq, abnormalReq]);

                console.log("[DEBUG] SystemPortalWidget API 响应:", {
                    totalRes: totalRes.data,
                    activeRes: activeRes.data,
                    abnormalRes: abnormalRes.data,
                });

                setOpsMetrics({
                    total: totalRes.data?.total || 0,
                    active: activeRes.data?.total || 0,
                    abnormal: abnormalRes.data?.total || 0,
                });
            } catch (error) {
                console.error("Failed to load portal metrics", error);
            }
        };

        fetchOpsMetrics();
    }, [hasOpsPermission]);

    // Module Definitions
    const modules = [
        {
            key: "ops",
            title: "运行控制 (OPS)",
            icon: <AirplaneOutlined style={{ fontSize: 32 }} />,
            description: "航班计划、航班监控、航段、航段管理",
            color: "#e6f7ff",
            borderColor: "#1890ff", // Darker for gradient
            metrics: [
                { label: "今日航班", value: opsMetrics.total },
                { label: "执行中", value: opsMetrics.active },
                { label: "异常状态", value: opsMetrics.abnormal },
            ],
            items: [
                { label: "新建航班计划", path: "/flightSchedule/editor/new", icon: <PlusCircleOutlined /> },
                { label: "航班监控", path: "/operation/flight-monitor", icon: <AirplaneOutlined /> },
                { label: "草稿计划", path: "/flightSchedule/drafts", icon: <FileTextOutlined /> },
                { label: "正式计划", path: "/flightSchedule/list", icon: <CalendarOutlined /> },
                { label: "航段管理", path: "/master-data/flight-legs", icon: <PartitionOutlined /> },
                { label: "航线管理", path: "/master-data/flight-routes", icon: <EnvironmentOutlined /> },
                { label: "通知中心", path: "/workspace/notification-center", icon: <TeamOutlined /> },
            ],
        },
        {
            key: "settlement",
            title: "结算中心 (FIN)",
            icon: <BankOutlined style={{ fontSize: 32 }} />,
            description: "成本核算、账单、AP对账",
            color: "#fffbe6",
            borderColor: "#faad14",
            items: [
                { label: "航班费用管理", path: "/settlement/leg-cost", icon: <CalculatorOutlined /> },
                { label: "账单上传解析", path: "/settlement/invoice-data-sources", icon: <FileSearchOutlined /> },
                { label: "航线批量计算", path: "/settlement/batch-calculations", icon: <TableOutlined /> },
                { label: "公共费用分摊", path: "/settlement/leg-cost?tab=cost-allocation", icon: <PartitionOutlined /> },
                { label: "成本报表管理", path: "/settlement/profit-reports", icon: <PieChartOutlined /> },
            ],
        },
        {
            key: "sales",
            title: "销售管理 (SALES)",
            icon: <ShopOutlined style={{ fontSize: 32 }} />,
            description: "主单号管理、销售航线",
            color: "#f9f0ff",
            borderColor: "#722ed1",
            items: [
                { label: "主单号管理", path: "/mawb/stock", icon: <ContainerOutlined /> },
                { label: "销售航线管理", path: "/settlement/sales-routes", icon: <EnvironmentOutlined /> },
            ],
        },
        {
            key: "procurement",
            title: "采购合同 (PROC)",
            icon: <ShoppingOutlined style={{ fontSize: 32 }} />,
            description: "供应商、采购订单、合同履行",
            color: "#fff0f6",
            borderColor: "#eb2f96",
            items: [
                { label: "客户", path: "/contract-management/business-partners", icon: <TeamOutlined /> },
                { label: "供应商合同", path: "/contract-management/supplier-contracts", icon: <FileTextOutlined /> },
                { label: "合同全览", path: "/contract-management/relationship-dashboard", icon: <AppstoreOutlined /> }, // 旧地址收下后再跳新页，不再打开旧关联查询
            ],
        },
        {
            key: "stats",
            title: "统计报表 (BI)",
            icon: <PieChartOutlined style={{ fontSize: 32 }} />,
            description: "运营报表、财务报表、大屏",
            color: "#f6ffed",
            borderColor: "#52c41a",
            items: [
                { label: "统计报表", path: "/settlement/statistics-dashboard", icon: <PieChartOutlined /> },
            ],
        },
        {
            key: "uld",
            title: "ULD管理 (ULD)",
            icon: <ContainerOutlined style={{ fontSize: 32 }} />,
            description: "库存、调拨、维修、追踪",
            color: "#e6fffb",
            borderColor: "#13c2c2",
            items: [
                { label: "ULD台账", path: "/uld-management", icon: <TableOutlined /> },
                { label: "库存预警", path: "/uld-stock-alert", icon: <AlertOutlined /> },
                { label: "UCM报文", path: "/ucm-management", icon: <MessageOutlined /> },
                { label: "出入库记录", path: "/uld-history", icon: <HistoryOutlined /> },
                { label: "统计分析", path: "/uld-statistics", icon: <PieChartOutlined /> },
            ],
        },
        {
            key: "basic",
            title: "基础数据 (MDM)",
            icon: <DatabaseOutlined style={{ fontSize: 32 }} />,
            description: "飞机、机场、航线、汇率",
            color: "#f5f5f5",
            borderColor: "#8c8c8c",
            items: [
                { label: "飞机资料", path: "/master-data/aircraft", icon: <AirplaneOutlined /> },
                { label: "机场资料", path: "/master-data/airports", icon: <EnvironmentOutlined /> },
                { label: "航段资料", path: "/master-data/flight-legs", icon: <PartitionOutlined /> },
                { label: "航线资料", path: "/master-data/flight-routes", icon: <DeploymentUnitOutlined /> },
                { label: "汇率管理", path: "/master-data/exchange-rates", icon: <DollarOutlined /> },
            ],
        },
        {
            key: "system",
            title: "系统管理 (SYS)",
            icon: <SettingOutlined style={{ fontSize: 32 }} />,
            description: "用户权限、系统配置、模板管理",
            color: "#fff1f0",
            borderColor: "#ff4d4f",
            items: [
                { label: "用户管理", path: "/user-management", icon: <UserOutlined /> },
                { label: "角色管理", path: "/role-management", icon: <TeamOutlined /> },
                { label: "权限管理", path: "/permission-management", icon: <SafetyCertificateOutlined /> },
                { label: "仪表盘模板", path: "/templates", icon: <LayoutOutlined /> },
            ],
        },
    ];

    // 根据用户权限过滤模块列表
    // 使用 useMemo 避免每次渲染重复计算
    const visibleModules = useMemo(() => {
        return modules.filter((mod) => {
            const requiredPermission = MODULE_PERMISSIONS[mod.key];
            // 如果模块没有定义权限要求（null/undefined），则默认显示
            if (!requiredPermission) {
                return true;
            }
            // 否则检查用户是否拥有该权限
            return hasPermission(requiredPermission);
        });
    }, [modules, hasPermission]);

    // 根据可见模块行数自动调整 widget 容器高度
    React.useEffect(() => {
        if (onRequestResize && visibleModules.length > 0) {
            const numRows = Math.ceil(visibleModules.length / 4);
            const desiredH = numRows * PORTAL_ROW_HEIGHT;
            onRequestResize(desiredH);
        }
    }, [visibleModules.length, onRequestResize]);

    const handleNavigate = (path) => {
        navigate(path);
    };

    const renderStyle = () => {
        switch (currentStyle) {
            case "vibrant":
                return <PortalStyleVibrant modules={visibleModules} onNavigate={handleNavigate} />;
            case "direct":
                return <PortalStyleDirect modules={visibleModules} onNavigate={handleNavigate} />;
            case "classic":
            default:
                return <PortalStyleClassic modules={visibleModules} onNavigate={handleNavigate} />;
        }
    };

    const styleMenuActions = [
        {
            key: "vibrant",
            label: "样式：现代",
            icon: <AppstoreOutlined />,
            onClick: () => setCurrentStyle("vibrant"),
        },
        {
            key: "classic",
            label: "样式：简约",
            icon: <BorderOuterOutlined />,
            onClick: () => setCurrentStyle("classic"),
        },
        {
            key: "direct",
            label: "样式：直达",
            icon: <LayoutOutlined />,
            onClick: () => setCurrentStyle("direct"),
        },
        {
            type: "divider",
        },
    ];

    return (
        <WidgetWrapper
            title="业务模块快速入口"
            showTitle={false} // Hide Title
            menuActions={styleMenuActions} // Menu Integration
            style={{ padding: 0, background: "transparent", border: "none" }}
            headerStyle={{ marginBottom: 0, paddingBottom: 4, background: "transparent" }}
            onRefresh={() => {}} // Enable refresh button to show menu
            bodyStyle={{ padding: 0 }} // Legacy padding removal
            styles={{ body: { padding: 0 } }} // Ant Design v5 padding removal
            {...restProps}
        >
            <div style={{ height: "100%", width: "100%" }}>{renderStyle()}</div>
        </WidgetWrapper>
    );
};

export default SystemPortalWidget;
